import React,
{
    useEffect,
    useRef,
    useState
}
from 'react';
import masterVolume from './masterVolume';
import
{
    createPreloadedAudio
}
from './audioPreload';

// IntroOverlay: preloads assets and plays the intro video from /Video/HeartIntro-site.mp4
export default function IntroOverlay(
{
    onComplete,
    onFadeStart,
    onAutoplayBlocked,
    onAutoplayAllowed,
    playRequestedKey = 0
})
{
    const videoRef = useRef(null);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [readyToPlay, setReadyToPlay] = useState(false);
    const [audioReady, setAudioReady] = useState(false);
    const [videoReady, setVideoReady] = useState(false);
    const [, setAutoplayBlocked] = useState(false);
    const [showLoader, setShowLoader] = useState(true);
    const [isFading, setIsFading] = useState(false);
    const loaderFadeTimerRef = useRef(null);
    // const [autoplayBlocked, setAutoplayBlocked] = useState(false); // removed: unused
    const FADE_SECONDS = 1.0; // fade trigger (seconds remaining) - start earlier
    const FADE_EARLY = 0.10; // make the visual fade finish this many seconds before the video end (increase to shorten visual fade)

    useEffect(() =>
    {
        let mounted = true;

        const loadAssets = async () =>
        {
            // assets to preload
            const images = ['/AriFloats.png', '/AriFloatsTAIL.png', '/AriBlink.png', '/Heart.png'];
            // base audio list used across the site (non-class specific)
            let audioUrls = [
                '/Audio/IntroLoop-Base.ogg',
                '/Audio/IntroLoop-Hover1.ogg',
                '/Audio/IntroLoop-Hover2.ogg',
                '/Audio/EnteringMain.ogg',
                '/Audio/Main-ButtonHover.ogg',
                '/Audio/Discord-TagAppear.ogg',
                '/Audio/WarningLoop.ogg',
                '/Audio/Main-Loop.ogg',
                '/Audio/Main-Loop_WINTER.ogg',
                // achievements
                '/Audio/AchievmentSpecial.ogg',
                '/Audio/AchievementSpecial.ogg',
                '/Audio/AchievementGeneric.ogg',
            ];

            // augment with class assets from siteGoobers.json (music + intro + optional images)
            try
            {
                const res = await fetch('/siteGoobers.json');
                const data = await res.json();
                if (Array.isArray(data))
                {
                    const extras = [];
                    const imageExtras = [];
                    for (const g of data)
                    {
                        try
                        {
                            const m = g && g.music ? String(g.music) : '';
                            const i = g && g.intro ? String(g.intro) : '';
                            const bg = g && g.bgImage ? String(g.bgImage) : '';
                            const floof = g && g.floofImage ? String(g.floofImage) : '';
                            if (m && !extras.includes(m)) extras.push(m);
                            if (i && !extras.includes(i)) extras.push(i);
                            if (bg && !imageExtras.includes(bg)) imageExtras.push(bg);
                            if (floof && !imageExtras.includes(floof)) imageExtras.push(floof);
                        }
                        catch (e)
                        {}
                    }
                    // merge unique
                    audioUrls = audioUrls.concat(extras.filter((src, idx, arr) => arr.indexOf(src) === idx));
                    images.push(...imageExtras.filter((src, idx, arr) => arr.indexOf(src) === idx));
                }
            }
            catch (e)
            {
                /* ignore: fall back to base audio list */ }

            const totalCount = images.length + audioUrls.length + 1; // +1 for video
            let loaded = 0;
            const bump = () =>
            {
                if (!mounted) return;
                setLoadingProgress(Math.round((loaded / totalCount) * 100));
            };

            // preload images
            images.forEach((src) =>
            {
                const img = new Image();
                img.src = src;
                img.onload = img.onerror = () =>
                {
                    loaded += 1;
                    bump();
                };
            });

            // preload audio (canplaythrough) and attach master volume control immediately
            let audioLoadedCount = 0;
            audioUrls.forEach((src) =>
            {
                try
                {
                    const a = createPreloadedAudio(src,
                    {
                        baseVolume: 1,
                        loop: false,
                        applyMaster: false,
                    });
                    if (!a) throw new Error('Failed to create preloaded audio element');
                    // apply master volume tracking so these are controlled right away
                    try
                    {
                        a._baseVolume = Number.isFinite(a.volume) ? a.volume : 1.0;
                    }
                    catch (e)
                    {
                        a._baseVolume = 1.0;
                    }
                    try
                    {
                        masterVolume.applyToElement(a);
                    }
                    catch (e)
                    {}
                    a.oncanplaythrough = () =>
                    {
                        audioLoadedCount += 1;
                        loaded += 1;
                        bump();
                        if (audioLoadedCount >= audioUrls.length)
                        {
                            setAudioReady(true);
                        }
                    };
                    a.onerror = () =>
                    {
                        audioLoadedCount += 1;
                        loaded += 1;
                        bump();
                        if (audioLoadedCount >= audioUrls.length)
                        {
                            setAudioReady(true);
                        }
                    };
                    try
                    {
                        a.load();
                    }
                    catch (e)
                    {}
                }
                catch (e)
                {
                    audioLoadedCount += 1;
                    loaded += 1;
                    bump();
                    if (audioLoadedCount >= audioUrls.length)
                    {
                        setAudioReady(true);
                    }
                }
            });

            // preload video
            const video = document.createElement('video');
            video.src = '/Video/HeartIntro-site.mp4';
            video.preload = 'auto';
            video.oncanplaythrough = () =>
            {
                if (!mounted) return;
                loaded += 1;
                bump();
                setVideoReady(true);
            };
            video.onerror = () =>
            {
                if (!mounted) return;
                loaded += 1;
                bump();
                setVideoReady(true);
            };

            return () =>
            {
                // clean up
                video.oncanplaythrough = null;
                video.onerror = null;
            };
        };

        const cleanupVideo = loadAssets();
        return () =>
        {
            mounted = false;
            try
            {
                typeof cleanupVideo === 'function' && cleanupVideo();
            }
            catch (e)
            {}
            if (loaderFadeTimerRef.current)
            {
                clearTimeout(loaderFadeTimerRef.current);
                loaderFadeTimerRef.current = null;
            }
        };
    }, []);

    // gate readiness: only allow video start when both audio and video are ready
    useEffect(() =>
    {
        if (audioReady && videoReady)
        {
            setReadyToPlay(true);
            setLoadingProgress(100);
        }
    }, [audioReady, videoReady]);

    useEffect(() =>
    {
        // on complete quickly fade out the loader text
        if (loadingProgress >= 100)
        {
            // keep loader visible briefly then fade
            loaderFadeTimerRef.current = setTimeout(() =>
            {
                setShowLoader(false);
                loaderFadeTimerRef.current = null;
            }, 600); // 600ms before fade
        }
    }, [loadingProgress]);

    useEffect(() =>
    {
        if (!readyToPlay) return;
        const v = videoRef.current;
        if (!v) return;

        let mounted = true;
        const tryPlay = async () =>
        {
            // ensure the intro video honors the persisted master volume
            try
            {
                if (typeof v._baseVolume === 'undefined') v._baseVolume = Number.isFinite(v.volume) ? v.volume : 1.0;
                try
                {
                    v.volume = v._baseVolume * masterVolume.getMasterVolume();
                }
                catch (e)
                {}
                try
                {
                    masterVolume.applyToElement(v);
                }
                catch (e)
                {}
            }
            catch (e)
            {}
            try
            {
                await v.play();
                if (!mounted) return;
                setAutoplayBlocked(false);
                try
                {
                    onAutoplayAllowed && onAutoplayAllowed();
                }
                catch (e)
                {}
            }
            catch (err)
            {
                // autoplay blocked
                setAutoplayBlocked(true);
                try
                {
                    onAutoplayBlocked && onAutoplayBlocked();
                }
                catch (e)
                {}
            }
        };
        tryPlay();

        const onEnded = () =>
        {
            if (!mounted) return;
            // unload video I NEED TO SEE THINGS
            try
            {
                v.pause();
                v.removeAttribute('src');
                v.load();
            }
            catch (e)
            {
                // ignore
            }
            onComplete && onComplete();
        };
        v.addEventListener('ended', onEnded);

        // fade the video when its remaining time is <= FADE_SECONDS
        const onTimeUpdate = () =>
        {
            try
            {
                const dur = v.duration;
                const now = v.currentTime;
                if (!isFinite(dur) || isNaN(dur)) return;
                const remaining = dur - now;
                if (remaining <= FADE_SECONDS && !isFading)
                {
                    setIsFading(true);
                    try
                    {
                        onFadeStart && onFadeStart();
                    }
                    catch (e)
                    {}
                }
            }
            catch (e)
            {
                // ignore
            }
        };
        v.addEventListener('timeupdate', onTimeUpdate);

        return () =>
        {
            mounted = false;
            v.removeEventListener('ended', onEnded);
            v.removeEventListener('timeupdate', onTimeUpdate);
            if (loaderFadeTimerRef.current)
            {
                clearTimeout(loaderFadeTimerRef.current);
                loaderFadeTimerRef.current = null;
            }
        };
    }, [readyToPlay, onComplete, onFadeStart, isFading, onAutoplayAllowed, onAutoplayBlocked]);

    // attempt playback on explicit request (from App)
    useEffect(() =>
    {
        if (!readyToPlay) return;
        const v = videoRef.current;
        if (!v) return;
        // playRequestedKey increments when App wants the video to try to play with a user gesture
        const tryPlayOnRequest = async () =>
        {
            // make sure master volume is applied in case it wasn't earlier bc shit is bonkers
            try
            {
                if (typeof v._baseVolume === 'undefined') v._baseVolume = Number.isFinite(v.volume) ? v.volume : 1.0;
                try
                {
                    v.volume = v._baseVolume * masterVolume.getMasterVolume();
                }
                catch (e)
                {}
                try
                {
                    masterVolume.applyToElement(v);
                }
                catch (e)
                {}
            }
            catch (e)
            {}
            try
            {
                await v.play();
                setAutoplayBlocked(false);
                try
                {
                    onAutoplayAllowed && onAutoplayAllowed();
                }
                catch (e)
                {}
            }
            catch (err)
            {
                setAutoplayBlocked(true);
                try
                {
                    onAutoplayBlocked && onAutoplayBlocked();
                }
                catch (e)
                {}
            }
        };
        // only attempt when playRequestedKey changes to a non-zero value
        if (playRequestedKey) tryPlayOnRequest();
    }, [playRequestedKey, readyToPlay, onAutoplayBlocked, onAutoplayAllowed]);

    return ( <div style = {
            {
                position: 'fixed',
                inset: 0,
                background: '#000',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 20000
            }
        } >
        <div style = {
            {
                width: '90%',
                maxWidth: 1100,
                background: 'transparent',
                textAlign: 'center'
            }
        } >
        <video ref = {
            videoRef
        }
        src = {
            '/Video/HeartIntro-site.mp4'
        }
        style = {
            {
                width: '100%',
                height: 'auto',
                display: 'block',
                margin: '0 auto',
                borderRadius: 6,
                opacity: isFading ? 0 : 1,
                transition: `opacity ${Math.max(0.05, FADE_SECONDS - FADE_EARLY)}s ease`,
            }
        }
        controls = {
            false
        }
        /> <div style = {
            {
                marginTop: 12,
                color: '#fff',
                minHeight: 28
            }
        } >
        {
            /* show loader only while still preloading */ }
        {
            showLoader && loadingProgress < 100 && ( <div style = {
                    {
                        marginBottom: 8,
                        transition: 'opacity 300ms ease'
                    }
                } > Loading: {loadingProgress}% </div>
            )
        }

        {
            /* autoplay state is handled by the global enable prompt in App */ } </div> </div> </div>
    );
}
